import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../data/siteContent";
import { ChapterHeading, ScrollReveal } from "./shared";

const PRINCIPLES = [
    {
        index: "01.1",
        title: "Precision First",
        body: "Every key, screw and shaft leaves our floor measured, checked and true to drawing. Tolerance is not a target here — it is the starting line.",
    },
    {
        index: "01.2",
        title: "Built to Endure",
        body: "Our parts go into machines that run for decades. We machine them accordingly — right material, right finish, right fit.",
    },
    {
        index: "01.3",
        title: "Relationships that Last",
        body: "Most of our clients have bought from us for twenty years or more. They stay because the part arrives right, on time, every time.",
    },
];

const Legacy = () => {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section id="legacy" data-testid="legacy-section" className="py-24 md:py-36 px-6 md:px-12">
            <ChapterHeading index="01" title="The Legacy" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-12 md:mt-16">
                <div className="md:col-span-5">
                    <ScrollReveal>
                        <h2 className="font-heading uppercase font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
                            Four decades.
                            <br />
                            <span className="text-outline-copper">One standard.</span>
                        </h2>
                    </ScrollReveal>
                    <ScrollReveal delay={0.15} className="mt-8">
                        <p className="text-base md:text-lg leading-relaxed text-[#595959] max-w-md">
                            For over 40 years, First Choice Trading Company has machined the parts
                            that keep India's heavy industries moving. What began as a small
                            Ghaziabad workshop is today a trusted supplier to wire drawing, rolling
                            mill, cement and gear box manufacturers across the country — a legacy
                            built one precise component at a time.
                        </p>
                    </ScrollReveal>

                    <div ref={imgRef} className="mt-12 relative overflow-hidden border border-[#D9D9D9] aspect-[4/3]">
                        <motion.img
                            src={IMAGES.legacy}
                            alt="Machinist at work on the shop floor"
                            style={{ y, scale: 1.18 }}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFA]/70 via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-4 text-[11px] uppercase tracking-[0.25em] text-[#595959]">
                            Shop floor — Ghaziabad, UP
                        </div>
                    </div>
                </div>

                <div className="md:col-span-7 flex flex-col justify-center">
                    {PRINCIPLES.map((p, i) => (
                        <ScrollReveal key={p.index} delay={i * 0.12}>
                            <div
                                data-testid={`legacy-principle-${i + 1}`}
                                className="group grid grid-cols-12 gap-4 md:gap-6 py-8 md:py-10 border-b border-[#E5E5E5] first:border-t items-start"
                            >
                                <span className="col-span-3 md:col-span-2 font-heading text-copper/80 text-sm tracking-[0.2em] pt-1.5">
                                    {p.index}
                                </span>
                                <div className="col-span-9 md:col-span-10">
                                    <h3 className="font-heading uppercase font-medium text-2xl sm:text-3xl tracking-tight group-hover:text-copper transition-colors duration-300">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm md:text-base leading-relaxed text-[#737373] max-w-xl">
                                        {p.body}
                                    </p>
                                </div>
                            </div>
                        </ScrollReveal>
                    ))}

                    <ScrollReveal delay={0.2} className="mt-10">
                        <p className="font-heading uppercase text-lg md:text-xl tracking-wide text-[#595959]">
                            "Our clients' machines carry our name in every rotating part —
                            <span className="text-copper"> that is a responsibility we have honoured for 40 years.</span>"
                        </p>
                    </ScrollReveal>
                </div>
            </div>
        </section>
    );
};

export default Legacy;
